import * as React from "react";
import { Users, Hash, Fullscreen, Minimize, Lock, Globe } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "./breadcrumb";
import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger, DialogFooter } from "./Dialog";
import { Input } from "./input";
import { Label } from "./label";
import { Switch } from "./switch";
import { Textarea } from "./textarea";
import { Badge } from "./badge";
import { X, Plus } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Trans, useTranslation } from "react-i18next";

export interface TeamChannelDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultExpanded?: boolean;
  triggerButton?: React.ReactNode;
  showTrigger?: boolean;
  onCreateChannel?: (data: TeamChannelData) => void | Promise<void>;
  availableUsers?: User[];
  // For editing existing channels
  initialData?: {
    name?: string;
    description?: string;
    isPublic?: boolean;
    selectedParticipants?: string[];
  };
}

export interface User {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
}

export interface TeamChannelData {
  id: string;
  name: string;
  description: string;
  participants: string[];
  isPublic: boolean;
}

export function TeamChannelDialog({
  open = false,
  onOpenChange,
  defaultExpanded = false,
  triggerButton,
  showTrigger = true,
  onCreateChannel,
  availableUsers = [],
  initialData,
}: TeamChannelDialogProps) {
  const { t } = useTranslation("team-chat");
  const [isOpen, setIsOpen] = React.useState(open);
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [channelName, setChannelName] = React.useState(initialData?.name || "");
  const [description, setDescription] = React.useState(initialData?.description || "");
  const [isPublic, setIsPublic] = React.useState(initialData?.isPublic ?? true);
  const [selectedParticipants, setSelectedParticipants] = React.useState<string[]>(
    initialData?.selectedParticipants || []
  );
  const [isParticipantPopoverOpen, setIsParticipantPopoverOpen] = React.useState(false);
  const [participantSearch, setParticipantSearch] = React.useState("");

  React.useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    // Reset form when dialog closes
    if (!newOpen) {
      setIsExpanded(defaultExpanded);
      setChannelName(initialData?.name || "");
      setDescription(initialData?.description || "");
      setIsPublic(initialData?.isPublic ?? true);
      setSelectedParticipants(initialData?.selectedParticipants || []);
      setParticipantSearch("");
    }
    onOpenChange?.(newOpen);
  };

  const handleCreate = async () => {
    if (!channelName.trim()) {
      return;
    }

    const data: TeamChannelData = {
      id: crypto.randomUUID(),
      name: channelName,
      description,
      participants: selectedParticipants,
      isPublic,
    };

    await onCreateChannel?.(data);
    handleOpenChange(false);
  };

  const handleAddParticipant = (userId: string) => {
    if (!selectedParticipants.includes(userId)) {
      setSelectedParticipants([...selectedParticipants, userId]);
    }
    setParticipantSearch("");
  };

  const handleRemoveParticipant = (userId: string) => {
    setSelectedParticipants(selectedParticipants.filter((id) => id !== userId));
  };

  const filteredUsers = availableUsers.filter(
    (user) =>
      !selectedParticipants.includes(user.id) &&
      (user.name.toLowerCase().includes(participantSearch.toLowerCase()) ||
        user.email?.toLowerCase().includes(participantSearch.toLowerCase()))
  );

  const selectedUsers = availableUsers.filter((user) => selectedParticipants.includes(user.id));

  const dialogContent = (
    <>
      <DialogTitle className="sr-only">{t("Create Team Channel")}</DialogTitle>
      <DialogDescription className="sr-only">
        {t("Create a new team channel with participants and settings.")}
      </DialogDescription>
      <div className={isExpanded ? "flex h-full flex-col" : "flex flex-col"}>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 !focus-visible:ring-0 !focus-visible:ring-transparent !focus-visible:border-transparent !ring-0 !ring-transparent !border-0 !outline-none"
            >
              {isExpanded ? <Minimize className="h-4 w-4" /> : <Fullscreen className="h-4 w-4" />}
            </Button>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>{t("New Team Channel")}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className={isExpanded ? "flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-2 pt-6" : "p-6"}>
          <TeamChannelDialogContent
            channelName={channelName}
            setChannelName={setChannelName}
            selectedUsers={selectedUsers}
            handleRemoveParticipant={handleRemoveParticipant}
            handleAddParticipant={handleAddParticipant}
            isParticipantPopoverOpen={isParticipantPopoverOpen}
            setIsParticipantPopoverOpen={setIsParticipantPopoverOpen}
            participantSearch={participantSearch}
            setParticipantSearch={setParticipantSearch}
            filteredUsers={filteredUsers}
            isPublic={isPublic}
            setIsPublic={setIsPublic}
            description={description}
            setDescription={setDescription}
          />
        </div>

        <DialogFooter className={isExpanded ? "border-t px-6 py-4" : "border-t px-6 py-3"}>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleCreate} disabled={!channelName.trim()}>
            {t("Create Channel")}
          </Button>
        </DialogFooter>
      </div>
    </>
  );

  if (isExpanded && isOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {showTrigger && (
          <DialogTrigger asChild>
            {triggerButton || (
              <Button size="sm">
                <Plus className="mr-2 h-4 w-4" />
                {t("Create Team Channel")}
              </Button>
            )}
          </DialogTrigger>
        )}
        <DialogContent
          className="overflow-hidden p-0 m-0 max-w-[calc(100%-16px)] max-h-[calc(100%-16px)] w-[calc(100%-16px)] h-[calc(100%-16px)]"
          style={{
            width: "calc(100% - 16px)",
            height: "calc(100% - 16px)",
            maxWidth: "calc(100% - 16px)",
            maxHeight: "calc(100% - 16px)",
          }}
        >
          {dialogContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <DialogTrigger asChild>
          {triggerButton || (
            <Button size="sm">
              <Plus className="mr-2 h-4 w-4" />
              {t("Create Team Channel")}
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="overflow-hidden p-0 md:max-h-[600px] md:max-w-[600px]">{dialogContent}</DialogContent>
    </Dialog>
  );
}

// Team Channel Content Component (following AccountDialog pattern)
export function TeamChannelDialogContent({
  channelName,
  setChannelName,
  selectedUsers,
  handleRemoveParticipant,
  handleAddParticipant,
  isParticipantPopoverOpen,
  setIsParticipantPopoverOpen,
  participantSearch,
  setParticipantSearch,
  filteredUsers,
  isPublic,
  setIsPublic,
  description,
  setDescription,
}: {
  channelName: string;
  setChannelName: (value: string) => void;
  selectedUsers: User[];
  handleRemoveParticipant: (userId: string) => void;
  handleAddParticipant: (userId: string) => void;
  isParticipantPopoverOpen: boolean;
  setIsParticipantPopoverOpen: (open: boolean) => void;
  participantSearch: string;
  setParticipantSearch: (value: string) => void;
  filteredUsers: User[];
  isPublic: boolean;
  setIsPublic: (isPublic: boolean) => void;
  description: string;
  setDescription: (value: string) => void;
}) {
  const { t } = useTranslation("team-chat");
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="channel-name">{t("Channel Name")}</Label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="channel-name"
              placeholder="e.g., marketing, engineering, design"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="participants">{t("Participants")}</Label>
          <div className="space-y-2">
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <Badge key={user.id} variant="secondary" className="flex items-center gap-1">
                    {user.name}
                    <button
                      onClick={() => handleRemoveParticipant(user.id)}
                      className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <Popover open={isParticipantPopoverOpen} onOpenChange={setIsParticipantPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <Plus className="mr-2 h-4 w-4" />
                  {t("Add participants")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder="Search users..."
                    value={participantSearch}
                    onValueChange={setParticipantSearch}
                  />
                  <CommandList>
                    <CommandEmpty>{t("No users found.")}</CommandEmpty>
                    <CommandGroup>
                      {filteredUsers.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.name}
                          onSelect={() => {
                            handleAddParticipant(user.id);
                            setIsParticipantPopoverOpen(false);
                          }}
                        >
                          <Users className="mr-2 h-4 w-4" />
                          <div className="flex flex-col">
                            <span>{user.name}</span>
                            {user.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                {isPublic ? (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
                <Label htmlFor="public-channel">{isPublic ? "Public Channel" : "Private Channel"}</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                {isPublic
                  ? "Anyone in your workspace can view and join this channel"
                  : "Only invited participants can view and access this channel"}
              </p>
            </div>
            <Switch id="public-channel" checked={isPublic} onCheckedChange={setIsPublic} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">
              <Trans ns="team-chat">
                Description
                <span className="text-muted-foreground ml-2 text-sm">(optional)</span>
              </Trans>
            </Label>
            <Textarea
              id="description"
              placeholder="What is this channel about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
